/**
 * Component for displaying popcorn detection and cleaning animation.
 *
 * @component
 * @param {Object} props - The properties passed to the PopcornDetection component.
 * @param {Object} props.data - Data containing information for popcorn detection and cleaning.
 * @returns {JSX.Element} - PopcornDetection component.
 */

import React, { useEffect, useState } from "react";
import Image from "next/image";
import ZigZag from "../../../assets/svg/ZigZagIcon";
import { motion, useAnimation, useInView } from "framer-motion";
import { useRef } from "react";
import MoppingIcon from "../../../assets/svg/MoppingIcon";

const PopcornDetection = ({ data }) => {
  const ref = useRef();
  const inView = useInView(ref, { once: true });

  const popcornDetectMessage = useAnimation();
  const popcornCleaningMessage = useAnimation();
  const popcornImage = useAnimation();
  const hotzonePopcornImage = useAnimation();

  useEffect(() => {
    let timerId = null;
    let hotzonePopcornImgTimerId = null;

    if (inView) {
      popcornDetectMessage.start({
        opacity: 1,
        transition: {
          delay: 0.5,
          duration: 1,
          type: "smooth",
        },
      });
      timerId = setTimeout(() => {
        hotzonePopcornImage.start({
          opacity: 1,
          scale: 1,
          transformOrigin: "center center",
          transition: {
            delay: 1,
            duration: 1,
            type: "spring",
          },
        });

        popcornCleaningMessage.start({
          opacity: 1,
          transition: {
            delay: 2,
            duration: 0.3,
            type: "smooth",
          },
        });

        popcornImage.start({
          opacity: 0,
          transition: {
            delay: 3,
            duration: 1,
            type: "smooth",
          },
        });

        popcornDetectMessage.start({
          opacity: 0,
          transition: {
            delay: 3,
            duration: 0.2,
            type: "smooth",
          },
        });

        hotzonePopcornImgTimerId = setTimeout(() => {
          hotzonePopcornImage.start({
            opacity: 0,
            scale: 0,
            transition: {
              delay: 1,
              duration: 1,
              type: "smooth",
            },
          });

          popcornCleaningMessage.start({
            opacity: 0,
            transition: {
              delay: 1,
              duration: 1,
              type: "smooth",
            },
          });
        }, 3000);
      }, 1500);
    }

    return () => {
      clearTimeout(timerId);
      clearTimeout(hotzonePopcornImgTimerId);
    };
  }, [inView]);

  return (
    <div className={`w-[23%] absolute right-[2%] top-[-125%] bigger:top-[-196%] bigger:right-[10%]`} ref={ref}>
      <div className="aspect-w-338 aspect-h-397">
        <div className="h-full w-full">
          <div className="h-full relative">
            <motion.div
              className={`absolute left-[34%] top-[7%] z-[1] flex justify-center items-center rounded-[10px] py-[7px] px-[14px] text-white bg-blue w-max`}
              initial={{ opacity: 0 }}
              animate={popcornDetectMessage}
            >
              &bull;
              <span className={`eyebrow-bold md:pl-[8px] pl-[6px]`}>
                {data?.popcornDetectedText}
              </span>
            </motion.div>
            <motion.div
              className={`absolute left-[34%] top-[7%] z-[1] flex justify-center items-center rounded-[10px] py-[7px] px-[14px] text-white bg-blue w-max`}
              initial={{ opacity: 0 }}
              animate={popcornCleaningMessage}
            >
              <MoppingIcon />
              <span className={`eyebrow-bold md:pl-[8px] pl-[6px]`}>
                {data?.vacuumingPopcornText}
              </span>
            </motion.div>
            <motion.div
              className={`w-full h-full absolute`}
              initial={{ opacity: 1 }}
              animate={popcornImage}
            >
              <div className="aspect-w-338 aspect-h-397">
                <Image
                  src={`https:${data?.popCornSpillImage?.fields?.file?.url}`}
                  layout="fill"
                  alt="Footprint Dirt Image"
                  className="custom-img"
                />
              </div>
            </motion.div>
            <motion.div
              style={{ originY: 0.5 }}
              className={`w-full absolute -z-[1] h-full`}
              initial={{ opacity: 0, scale: 0 }}
              animate={hotzonePopcornImage}

            >
              <div className="aspect-w-338 aspect-h-397">
                <Image
                  src={`https:${data?.popCornHotzoneImage?.fields?.file?.url}`}
                  layout="fill"
                  alt="Hotzone - Footprint Dirt animation"
                  className="custom-img"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PopcornDetection;
